import { type InjectionKey, reactive, ref } from "vue";
import type { DeckEvent, DeckHtmlAttributes, DeckProperties, DeckUpdateEvent } from "@/types";
import type { Logger } from "@/stores/logger";
import { propertyToHtmlAttribute } from "@/utils/casing";

export type AppStore = ReturnType<typeof useAppStore>;
export const APP: InjectionKey<AppStore> = Symbol("APP");

export const useAppStore = (log: Omit<Logger, "logId"> = console) => {
  const dev = ref(false);
  const active = ref(false);
  const appRef = ref<HTMLElement>();
  const prStringHtmlAttribute = ref<string | undefined>();
  const cameraHtmlAttribute = ref<string | undefined>();
  const cameraFlightHtmlAttribute = ref<string | undefined>();
  const multisessionDisconnectHtmlAttribute = ref(false);
  const consumptionDataHtmlAttribute = ref<string | undefined>();
  const hideConsumptionDataHtmlAttribute = ref(false);
  const isFullscreenSupported = ref(true);
  const isFullscreenActive = ref(false);
  const enterFullscreen = ref<() => void>();
  const exitFullscreen = ref<() => void>();

  /**
   * Custom event emitter
   */
  function emitEvent(deckEvent: DeckEvent) {
    log.debug(`Emitting event '${deckEvent.type}' with detail`, deckEvent.detail);
    appRef.value?.dispatchEvent(
      new CustomEvent<typeof deckEvent.detail>(deckEvent.type, {
        bubbles: true, // = propagate upwards in the DOM tree
        composed: true, // = propagate across the shadow DOM boundary
        detail: deckEvent.detail
      })
    );
  }

  function updateHtmlAttribute<P extends keyof DeckHtmlAttributes>(
    property: P,
    newValue: DeckHtmlAttributes[P]
  ) {
    log.debug(`Updating HTML attribute '${property}' to '${newValue}'`);
    const host = (appRef.value?.getRootNode() as ShadowRoot)?.host;

    if (!host) {
      // host should always be defined, but just in case
      return;
    }

    // update HTML attributes to stay synchronized with the internal update
    if (typeof newValue === "boolean") {
      if (newValue) {
        host.setAttribute(property, "");
      } else {
        host.removeAttribute(property);
      }
    } else {
      host.setAttribute(property, newValue!);
    }
  }

  /**
   * This emitter tracks changes to attributes (like active true/false changes)
   */
  function emitAttributeChange<P extends keyof DeckProperties>(
    property: P,
    newValue: DeckProperties[P]
  ) {
    emitEvent({
      type: `update:${propertyToHtmlAttribute(property)}`,
      detail: { value: newValue }
    } as DeckUpdateEvent);
    updateHtmlAttribute(propertyToHtmlAttribute(property), newValue);
  }

  function emitCloseEvent(): void {
    emitAttributeChange("active", false);
  }

  return reactive({
    dev: dev,
    active: active,
    prStringHtmlAttribute: prStringHtmlAttribute,
    cameraHtmlAttribute: cameraHtmlAttribute,
    cameraFlightHtmlAttribute: cameraFlightHtmlAttribute,
    multisessionDisconnectHtmlAttribute: multisessionDisconnectHtmlAttribute,
    instance: appRef,
    isFullscreenActive: isFullscreenActive,
    isFullscreenSupported: isFullscreenSupported,
    enterFullscreen: enterFullscreen,
    exitFullscreen: exitFullscreen,
    hideConsumptionDataHtmlAttribute: hideConsumptionDataHtmlAttribute,
    consumptionDataHtmlAttribute: consumptionDataHtmlAttribute,
    emitAttributeChange: emitAttributeChange,
    emitCloseEvent: emitCloseEvent,
    emitEvent: emitEvent
  });
};
